/**
 * POM-6534 — the Review Lot & NDC filter bar: what each filter matches and what each list offers.
 *
 * Every option comes from the rows in the batch, so there is nothing to look up. One filter bar
 * narrows every group of the grid, and filtering never touches the uncommitted changes.
 */

import { ROW_STATUS, effectiveNdcFor } from './bulkLotNdcStatus';
import { statusLabelFor } from './bulkLotNdcStatusLabels';
import { ndcDigits } from './bulkOverrideFilters';

export const ALL = 'All';

export const NO_REVIEW_FILTERS = {
  drug: ALL,
  ndc: ALL,
  facility: ALL,
  partner: ALL,
  status: ALL,
};

export const compareAlphabetically = (left, right) =>
  String(left).localeCompare(String(right), undefined, { sensitivity: 'base' });

export const hasActiveReviewFilters = ({ filters, search }) =>
  Object.keys(NO_REVIEW_FILTERS).some((field) => filters[field] !== ALL) || !!search.trim();

export const rowMatchesReviewFilters = ({ row, change, filters, search }) => {
  const { item, status } = row;

  // A staged edit stays in view under any filter: the edit itself can change the NDC or status
  // being filtered on, and a row that vanished on Apply could not be checked or undone.
  if (status === ROW_STATUS.CHANGED) return true;

  if (filters.drug !== ALL && item.medicationName !== filters.drug) return false;
  if (filters.facility !== ALL && item.fulfillingPharmacyName !== filters.facility) return false;
  if (filters.partner !== ALL && item.partner !== filters.partner) return false;
  // The chip's own words: one status can read "Lot expired", "Partial" or "Lot unavailable".
  if (filters.status !== ALL && statusLabelFor(row) !== filters.status) return false;
  // The NDC the grid shows, staged edit included, compared on digits so a hyphenated and a bare
  // spelling of one NDC are the same filter.
  if (
    filters.ndc !== ALL &&
    ndcDigits(effectiveNdcFor({ item, change })) !== ndcDigits(filters.ndc)
  ) {
    return false;
  }

  const needle = search.trim().toLowerCase();
  if (!needle) return true;

  return `${item.orderId ?? ''} ${item.rxNumber ?? ''}`.toLowerCase().includes(needle);
};

/**
 * A list sorted A–Z, still holding the value currently selected. A value that drops out of the
 * batch (a status cleared by a commit, an NDC changed by an edit) would otherwise leave its field
 * holding something its own list no longer offers.
 */
const sortedWithSelected = (values, selected) =>
  [...new Set([...values, ...(selected === ALL ? [] : [selected])].filter(Boolean))].sort(
    compareAlphabetically
  );

/**
 * One option per NDC, de-duplicated on digits, each carrying the drug it belongs to so the number
 * is never the only clue.
 */
const ndcOptionsFor = ({ rows, changes, selected }) => {
  const optionByDigits = new Map();
  rows.forEach(({ item }) => {
    const ndc = effectiveNdcFor({ item, change: changes[item.requestLineItemId] });
    const digits = ndcDigits(ndc);
    if (digits && !optionByDigits.has(digits)) {
      optionByDigits.set(digits, { ndc, drugName: item.medicationName ?? '' });
    }
  });

  if (selected !== ALL && !optionByDigits.has(ndcDigits(selected))) {
    optionByDigits.set(ndcDigits(selected), { ndc: selected, drugName: '' });
  }

  return [...optionByDigits.values()].sort((left, right) =>
    compareAlphabetically(left.ndc, right.ndc)
  );
};

export const buildReviewFilterOptions = ({ rows, changes, filters }) => ({
  drugs: sortedWithSelected(
    rows.map(({ item }) => item.medicationName),
    filters.drug
  ),
  ndcs: ndcOptionsFor({ rows, changes, selected: filters.ndc }),
  facilities: sortedWithSelected(
    rows.map(({ item }) => item.fulfillingPharmacyName),
    filters.facility
  ),
  partners: sortedWithSelected(
    rows.map(({ item }) => item.partner),
    filters.partner
  ),
  // Pending commit rows are never filtered, so their label is not offered.
  statuses: sortedWithSelected(
    rows.filter((row) => row.status !== ROW_STATUS.CHANGED).map(statusLabelFor),
    filters.status
  ),
});
